import { FC, useEffect, useRef } from "react";
import { Button, Form, Input, InputRef, Modal, message } from "antd";
import { RankingProjectService } from "modules/organization/services/rankingProject.service";
import { ProjectListService } from "modules/organization/services";
import { useProjectDetailsStore } from "modules/organization/store";

interface IExportRankingProjectFormData {
  question_text: string;
  question_variable?: string;
}

const EXPORT_RANKS_TOAST_KEY = "exportRanksIntoProjectKey";

const ExportRankingProjectModal: FC<{
  open: boolean;
  projectSlug?: string;
  rankingProjectId: number | null;
  rankingProjectTitle: string;
  isTemplateBased: boolean;
  exportVariableEditable: boolean;
  existingExportVariable: string | null;
  onClose: () => void;
}> = ({
  open,
  projectSlug,
  rankingProjectId,
  rankingProjectTitle,
  isTemplateBased,
  exportVariableEditable,
  existingExportVariable,
  onClose,
}) => {
  const [form] = Form.useForm<IExportRankingProjectFormData>();
  const questionTextInputRef = useRef<InputRef | null>(null);
  const { getProjectDetails } = useProjectDetailsStore();

  const doExportRanksIntoProject = async (
    formValues: IExportRankingProjectFormData,
  ) => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    // research.md R9: reuse the app's existing variable-uniqueness check verbatim, the same way
    // QuestionAddEditForm.tsx's validateUniqueVariable already does — only meaningful when this is
    // the first export for this Ranking Type (the field is disabled/ignored otherwise).
    if (exportVariableEditable && formValues.question_variable?.trim()) {
      try {
        const { data } =
          await new ProjectListService().checkUniqueQuestionVariable({
            question_variable: formValues.question_variable.trim(),
            project_slug: projectSlug,
          });
        if (data.data.exists) {
          form.setFields([
            { name: "question_variable", errors: ["Variable already taken"] },
          ]);
          return;
        }
      } catch (error) {
        console.error(error);
        message.error("Something went wrong, please try again.");
        return;
      }
    }
    onClose();
    message.open({
      key: EXPORT_RANKS_TOAST_KEY,
      content: "Exporting ranks into project...",
      type: "loading",
      duration: 0,
    });
    try {
      await new RankingProjectService().exportRanksIntoProject(
        projectSlug,
        rankingProjectId,
        formValues.question_text,
        exportVariableEditable ? formValues.question_variable : undefined,
      );
      await getProjectDetails(projectSlug);
      message.success("Ranking results exported successfully.");
    } catch (error: any) {
      const backendMessage = error?.error?.response?.data?.message;
      const contentMessage =
        !backendMessage && error instanceof Error ? error.message : null;
      message.error(
        backendMessage || contentMessage || "Failed to export ranking results.",
      );
    } finally {
      message.destroy(EXPORT_RANKS_TOAST_KEY);
    }
  };

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        question_text: isTemplateBased
          ? `Which medal was achieved in "${rankingProjectTitle}"?`
          : `What score was achieved in "${rankingProjectTitle}"?`,
        question_variable: exportVariableEditable
          ? undefined
          : (existingExportVariable ?? undefined),
      });
      questionTextInputRef.current?.focus();
    }
  }, [
    open,
    form,
    rankingProjectTitle,
    isTemplateBased,
    exportVariableEditable,
    existingExportVariable,
  ]);

  return (
    <Modal
      className="confirmationModal"
      wrapClassName="addModal"
      title={<h2>Export Ranks into Project</h2>}
      centered
      open={open}
      onCancel={onClose}
      footer={[
        <Button htmlType="button" key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          type="primary"
          onClick={() => form.submit()}
          htmlType="button"
          key="export"
        >
          Export
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          doExportRanksIntoProject(values).catch(console.error);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            form.submit();
          }
        }}
      >
        <Form.Item
          name="question_text"
          label="Question"
          rules={[
            { required: true, message: "Question must not be empty" },
            { whitespace: true, message: "Question must not be empty" },
          ]}
        >
          <Input maxLength={500} ref={questionTextInputRef} />
        </Form.Item>
        <Form.Item
          name="question_variable"
          label="Question Variable"
          extra={
            !exportVariableEditable
              ? "This ranking type has already been exported — the variable name can no longer be changed."
              : undefined
          }
          validateFirst={"parallel"}
          rules={
            exportVariableEditable
              ? [
                  {
                    required: true,
                    message: "Question variable must not be empty",
                  },
                  {
                    whitespace: true,
                    message: "Question variable must not be empty",
                  },
                  {
                    pattern: /^[A-Za-z0-9]/,
                    message:
                      "Variable should start with alphanumeric characters.",
                  },
                  {
                    pattern: /^[A-Za-z0-9-_]+$/,
                    message:
                      "Only alphanumeric characters, underscore and hyphen are allowed.",
                  },
                  {
                    pattern: /^[A-Za-z0-9-_]{3,}$/,
                    message: "Variable should be minimum three characters.",
                  },
                ]
              : []
          }
        >
          <Input maxLength={100} disabled={!exportVariableEditable} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportRankingProjectModal;
